import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import { Breadcrumb } from "../../../general/modules/commons/breadcrumb/breadcrumb";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import { RelationMutateNavigation } from "../features/RelationMutateNavigation";
import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import { FileUpload } from "../../../general/modules/commons/fileUpload/FileUpload";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import {
  importPlateNumbers,
  ImportResult,
} from "../../infrastructure/slices/ImportSlice";
import "../../../general/modules/commons/fileUpload/FileUpload.scss";

export const RelationImportPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("common");
  const { id } = useParams();

  const currentUser = useAppSelector((state) => state.auth.user);

  if (
    !currentUser ||
    (currentUser.role !== "super-admin" && currentUser.role !== "dbh-manager")
  ) {
    return (
      <Layout
        header={
          <LayoutHeader logout={() => void dispatch(logout())}>
            <LayoutHeaderTitle title={t("access.denied")} />
            <Breadcrumb></Breadcrumb>
          </LayoutHeader>
        }
      >
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("access.denied")} />
            <p className={"mt-3"}>{t("access.no_permission")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );
  }

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
    setImportResult(null);
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !id) {
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(
        importPlateNumbers({
          file: selectedFile,
          relationId: id,
        }),
      );

      if (importPlateNumbers.fulfilled.match(result)) {
        setImportResult(result.payload);
      } else {
        setImportResult({
          success: false,
          message:
            t("import.failed") +
            ": " +
            (result.error?.message || t("error.unknown")),
          processed_count: 0,
          error_count: 1,
          errors: [],
        });
      }
    } catch (error) {
      console.error("Import failed:", error);
      setImportResult({
        success: false,
        message:
          t("import.failed") +
          ": " +
          (error instanceof Error ? error.message : t("error.unknown")),
        processed_count: 0,
        error_count: 1,
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setImportResult(null);
  };

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={t("relation.mutate.import")} />
          <Breadcrumb></Breadcrumb>
          <LayoutHeaderActions>
            {selectedFile && !loading && (
              <Button
                onClick={() => void handleImport()}
                variant="primary"
                className="me-2"
              >
                {t("import.button")}
              </Button>
            )}
            {importResult && (
              <Button
                onClick={resetImport}
                variant="secondary"
                className="me-2"
              >
                {t("import.reset_button")}
              </Button>
            )}
          </LayoutHeaderActions>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <RelationMutateNavigation />
        <Content>
          <ContentHeader title={t("import.page_title")} />
          <div className="mb-4">
            <FileUpload
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              accept=".xlsx,.xls"
              disabled={loading}
              maxSize={10}
              allowedTypes={[".xlsx", ".xls"]}
              description={t("import.description")}
            />
          </div>
          {loading && (
            <Alert variant="warning" className="mt-3">
              <div className="d-flex align-items-center justify-content-center py-4 mt-3">
                <Spinner
                  animation="border"
                  variant="primary"
                  className="me-2"
                />
                <span className="text-muted">{t("import.processing")}</span>
              </div>
            </Alert>
          )}
          {importResult && (
            <div className="mb-4">
              <Alert
                variant={importResult.success ? "success" : "danger"}
                className="d-flex align-items-start"
              >
                <div className="me-3 mt-1">
                  {importResult.success ? (
                    <CheckCircle size={20} />
                  ) : (
                    <XCircle size={20} />
                  )}
                </div>
                <div className="flex-grow-1">
                  <Alert.Heading as="h6" className="mb-2">
                    {importResult.success
                      ? t("import.success")
                      : t("import.failed")}
                  </Alert.Heading>
                  <p className="mb-2">{importResult.message}</p>
                  <small>
                    {t("import.processed")}: {importResult.processed_count} |{" "}
                    {t("import.errors")}: {importResult.error_count}
                  </small>
                </div>
              </Alert>
              {importResult.errors && importResult.errors.length > 0 && (
                <Alert variant="warning" className="mt-3">
                  <div className="d-flex align-items-start">
                    <div className="me-2 mt-1">
                      <XCircle size={20} />
                    </div>
                    <div>
                      <Alert.Heading as="h6" className="mb-2">
                        {t("import.error_list_title")}:
                      </Alert.Heading>
                      <ul className="mb-0 ps-3">
                        {importResult.errors.map((error, index) => (
                          <li key={index} className="text-danger">
                            {t("import.row")} {error.row}: {error.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Alert>
              )}
            </div>
          )}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
